/**
 * Export Results — the right panel of the Results tab. OWNER: workstream F.
 *
 * The format descriptions name the files `export_results` actually copies out of
 * `<outputRoot>/<subjectId>/`, taken from a real run, rather than a generic
 * "report and images" blurb. If a case only ran DWI, the PWI file names simply
 * will not be there; the panel says what a format contains, not what it promises.
 *
 * TODO(workstream C): registerTab("results", { centre: ResultsTab, right: ExportPanel }).
 */

import * as React from "react";
import { AlertTriangle, Check, FolderOpen, Loader2, Upload } from "lucide-react";
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  Panel,
  Separator,
} from "@/components/ui/primitives";
import { EXPORT_FORMATS, useResultsStore, type ExportFormat } from "@/results/state";
import { SourceNote } from "@/results/ui";
import { baseName } from "@/results/format";

export interface FormatSpec {
  id: ExportFormat;
  label: string;
  summary: string;
  /** Exactly what lands in the destination directory, from a real output tree. */
  contents: string[];
}

/**
 * Shared with File → Export so the two surfaces cannot drift. The PDF list is
 * exhaustive: a DWI run writes interpretation PDFs for ASPECTS, ASPECTSPC, LOBE
 * and VASCULAR only — VENTRICLES and BPM_TYPE1 get CSVs but no PDF.
 */
export const FORMAT_SPECS: FormatSpec[] = [
  {
    id: "pdf",
    label: "PDF Report",
    summary: "SHAP interpretation PDFs written by the reporting stage.",
    contents: [
      "*_VASCULAR_report_interpretation.pdf",
      "*_ASPECTS_report_interpretation.pdf",
      "*_ASPECTSPC_report_interpretation.pdf",
      "*_LOBE_report_interpretation.pdf",
    ],
  },
  {
    id: "csv",
    label: "CSV Table",
    summary: "Atlas quantification tables — QFV fractions and raw lesion loads.",
    contents: [
      "DWI: *_VASCULAR_QFV.csv, *_LOBE_QFV.csv, *_ASPECTS_QFV.csv, *_ASPECTSPC_QFV.csv, *_VENTRICLES_QFV.csv, *_BPM_TYPE1_QFV.csv",
      "DWI: the matching *_lesionload.csv files",
      "PWI: *_Vascular_HPQFV.csv, *_Lobe_HPQFV.csv, *_ASPECTS_HPQFV.csv … and *_HPload.csv",
    ],
  },
  {
    id: "nifti",
    label: "NIfTI Images",
    summary: "Segmentation masks in native and MNI152 space.",
    contents: [
      "*_stroke-mask.nii.gz (native)",
      "*_stroke-mask_space-MNI152.nii.gz, *_stroke-mask_space-MNI152_affsyn.nii.gz",
      "PWI: the hypoperfusion mask from the PWI segmentation directory",
    ],
  },
  {
    id: "json",
    label: "JSON Summary",
    summary: "Machine-readable metrics and the summary shown in this tab.",
    contents: [
      "*_metrics.json — dice / precision / sensitivity / sdr and voxel counts, per space",
      "the results summary (volumes, ASPECTS, mismatch, file paths)",
    ],
  },
];

function FormatRow({ spec }: { spec: FormatSpec }) {
  const checked = useResultsStore((s) => s.exportFormats[spec.id]);
  const toggle = useResultsStore((s) => s.toggleExportFormat);
  const [open, setOpen] = React.useState(false);

  return (
    <div className="rounded border border-neutral-200 px-2.5 py-2">
      <label className="flex cursor-pointer items-start gap-2">
        <Checkbox
          className="mt-0.5"
          checked={checked}
          onChange={(e) => toggle(spec.id, e.currentTarget.checked)}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-medium text-neutral-800">{spec.label}</span>
          <span className="block text-[11px] leading-tight text-neutral-500">{spec.summary}</span>
        </span>
      </label>
      <button
        type="button"
        className="mt-1 pl-5 text-[11px] text-blue-700 hover:underline"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide file list" : "What is written?"}
      </button>
      {open ? (
        <ul className="mt-1 space-y-0.5 pl-5">
          {spec.contents.map((line) => (
            <li key={line} className="font-mono text-[10px] leading-tight text-neutral-500">
              {line}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function ExportPanel() {
  const caseManifest = useResultsStore((s) => s.caseManifest);
  const formats = useResultsStore((s) => s.exportFormats);
  const exportDir = useResultsStore((s) => s.exportDir);
  const chooseExportDir = useResultsStore((s) => s.chooseExportDir);
  const openAfter = useResultsStore((s) => s.openAfterExport);
  const setOpenAfter = useResultsStore((s) => s.setOpenAfterExport);
  const exporting = useResultsStore((s) => s.exporting);
  const written = useResultsStore((s) => s.exportWritten);
  const exportError = useResultsStore((s) => s.exportError);
  const runExport = useResultsStore((s) => s.runExport);

  const selected = EXPORT_FORMATS.filter((f) => formats[f]);
  const defaultDir = caseManifest ? `${caseManifest.outputRoot}/${caseManifest.subjectId}` : null;

  if (!caseManifest) {
    return (
      <Panel title="Export Results">
        <EmptyState title="No case is open" hint="Open a case to export its results." />
      </Panel>
    );
  }

  return (
    <Panel title="Export Results" bodyClassName="p-3 space-y-3">
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-neutral-700">Format</p>
        {FORMAT_SPECS.map((spec) => (
          <FormatRow key={spec.id} spec={spec} />
        ))}
      </div>

      <Separator />

      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-neutral-700">Destination</p>
        <div className="flex items-center gap-1.5">
          <Input
            value={exportDir ?? ""}
            readOnly
            placeholder={defaultDir ?? "Choose a directory…"}
            title={exportDir ?? defaultDir ?? ""}
            className="font-mono text-[11px]"
          />
          <Button size="sm" onClick={() => void chooseExportDir()} title="Choose output directory">
            <FolderOpen className="h-3.5 w-3.5" />
          </Button>
        </div>
        <label className="flex items-center gap-2 pt-0.5">
          <Checkbox checked={openAfter} onChange={(e) => setOpenAfter(e.currentTarget.checked)} />
          <span className="text-xs text-neutral-700">Open folder after export</span>
        </label>
      </div>

      <Separator />

      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-neutral-700">File Naming</p>
        <label className="flex items-center gap-2">
          {/* `export_results` takes {caseId, destDir, formats} only — there is no
              naming option in the contract, so the control states the rule rather
              than pretending to change it. */}
          <Checkbox checked readOnly disabled />
          <span className="text-xs text-neutral-500">Keep the pipeline's file names</span>
        </label>
        <Input disabled placeholder={`${caseManifest.subjectId}_…`} className="font-mono text-[11px]" />
        <SourceNote>
          Exported files keep their `{caseManifest.subjectId}_…` names so they stay matched to the run
          that produced them. A custom prefix needs a contract change — `export_results` accepts no
          naming options in v1.
        </SourceNote>
      </div>

      <Separator />

      <div className="space-y-2">
        <Button
          variant="primary"
          className="w-full"
          disabled={selected.length === 0 || exporting}
          onClick={() => void runExport()}
        >
          {exporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {exporting ? "Exporting…" : `Export Selected (${selected.length})`}
        </Button>

        {exportError ? (
          <div className="flex items-start gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0 text-red-600" />
            <span className="text-[11px] text-red-800">{exportError}</span>
          </div>
        ) : null}

        {written && written.length > 0 ? (
          <div className="rounded border border-green-200 bg-green-50 px-2 py-1.5">
            <p className="flex items-center gap-1.5 text-[11px] font-medium text-green-800">
              <Check className="h-3.5 w-3.5" />
              {written.length} file{written.length === 1 ? "" : "s"} written
            </p>
            <ul className="mt-1 max-h-32 space-y-0.5 overflow-auto">
              {written.map((path) => (
                <li key={path} className="truncate font-mono text-[10px] text-green-900" title={path}>
                  {baseName(path)}
                </li>
              ))}
            </ul>
          </div>
        ) : written ? (
          <SourceNote>
            The backend reported no files. Nothing matching the selected formats was found in this
            case's output directory.
          </SourceNote>
        ) : null}
      </div>
    </Panel>
  );
}

export default ExportPanel;
